const axios = require('axios');
const DojoReport = require('@/lib/PaymentMethods/Dojo/DojoReport');
const DojoReportNotifications = require('@/lib/PaymentMethods/Dojo/DojoReportNotifications');
const DojoTerminalDetailsRequestRecord = require('./Records/Request/DojoTerminalDetailsRequestRecord');
const DojoTerminalListRequestRecord = require('./Records/Request/DojoTerminalListRequestRecord');
const DojoTerminalReportPollRequestRecord = require('./Records/Request/DojoTerminalReportPollRequestRecord');
const DojoTerminalReportRequestRecord = require('./Records/Request/DojoTerminalReportRequestRecord');
const DojoTransaction = require('@/lib/PaymentMethods/Dojo/DojoTransaction');
const DojoTransactionDeleteRequestRecord = require('./Records/Request/DojoTransactionDeleteRequestRecord');
const DojoTransactionNotifications = require('@/lib/PaymentMethods/Dojo/DojoTransactionNotifications');
const DojoTransactionPollRequestRecord = require('./Records/Request/DojoTransactionPollRequestRecord');
const DojoTransactionRefundRequestRecord = require('./Records/Request/DojoTransactionRefundRequestRecord');
const DojoTransactionSaleRequestRecord = require('./Records/Request/DojoTransactionSaleRequestRecord');
const DojoTransactionSignatureRequestRecord = require('./Records/Request/DojoTransactionSignatureRequestRecord');
const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');
const PaymentMethodRef = require('@/lib/PaymentMethods/PaymentMethodRef');

class DojoDriver {
    /**
     * @constructor
     *
     * @param $timeout
     * @param {DojoConfig} dojoConfig
     * @param {CurrentDevice} currentDevice
     */
    constructor(
        $timeout,
        dojoConfig,
        currentDevice,
    ) {
        this.$timeout = $timeout;

        /**
         * @type {ConsoleLogger}
         */
        this.logger = logFactory('DojoDriver');

        /**
         * @type {CurrentDevice}
         */
        this.currentDevice = currentDevice;

        /**
         * @type {string}
         */
        this.paymentMethodRef = PaymentMethodRef.CARD_DOJO_CHIP_AND_PIN;

        /**
         * @type {DojoConfig}
         */
        this.config = dojoConfig;

        /**
         * @type {AxiosInstance}
         */
        this.axiosInstance = null;
    }

    /**
     * Should we validate the terminalId or not?
     *
     * @param {boolean} validateTerminalId
     */
    init(validateTerminalId = true) {
        this.config.validate(validateTerminalId);
        this.axiosInstance = this.createAxiosInstance();
    }

    /**
     * Create an axios instance with all the required headers and baseURL set.
     *
     * @returns {AxiosInstance}
     */
    createAxiosInstance() {
        return axios.create({
            baseURL: this.config.terminalHost,
            auth: {
                username: this.config.username,
                password: this.config.apiKey,
            },
            headers: {
                'Software-House-Id': this.config.softwareHouseId,
                'Installer-Id': this.config.installerId,
                'Accept': this.config.apiAcceptVersion,
            },
        });
    }

    /**
     * Check if the terminal for this device is online and available for use.
     */
    async terminalReady() {
        try {
            const response = await this.axiosInstance.request(
                new DojoTerminalDetailsRequestRecord(this.config)
            );
            if (response.data.status === 'AVAILABLE') {
                return true;
            }
            // BUSY
            return false;
        } catch (error) {
            this.handleError(error);
            return false;
        }
    }

    async getTerminalList() {
        try {
            const response = await this.axiosInstance.request(
                new DojoTerminalListRequestRecord()
            );
            return response.data;
        } catch (error) {
            this.handleError(error);
            return null;
        }
    }

    /**
     * @param {DojoReportType} reportType
     *
     * @return {Promise<*|null>}
     */
    createReportRequest(reportType) {
        const request = new DojoTerminalReportRequestRecord(this.config, reportType);
        return new DojoReport(request);
    }

    /**
     * Create a new DojoTransaction object.
     *
     * @param {number} amount
     * @param {{cardholderNotPresent?: boolean, amountCashback?: number}} extraPayload
     *
     * @return {DojoTransaction}
     */
    createTransactionRequest(amount, extraPayload = {}) {
        let request = null;
        let payload = {
            amount,
        };
        if (extraPayload.cardholderNotPresent) {
            payload.cardholderNotPresent = extraPayload.cardholderNotPresent;
        }
        if (amount >= 0) {
            if (extraPayload.amountCashback) {
                payload.amountCashback = extraPayload.amountCashback;
            }
            request = new DojoTransactionSaleRequestRecord(
                this.config,
                payload,
            );
        } else {
            request = new DojoTransactionRefundRequestRecord(
                this.config,
                payload,
            );
        }

        return new DojoTransaction(request, amount);
    }

    /**
     * @param {DojoTransaction} dojoTransaction
     *
     * @return {Promise<boolean>}
     */
    async sendTransaction(dojoTransaction) {
        try {
            const response = await this.axiosInstance.request(dojoTransaction.initialRequest);
            if (response.status === 201 && response.data && response.data.requestId) {
                dojoTransaction.requestId = response.data.requestId;
                return true;
            }
            return false;
        } catch (error) {
            this.handleError(error);
            return false;
        }
    }

    /**
     * @param {DojoReport} dojoReport
     *
     * @return {Promise<boolean>}
     */
    async sendReportRequest(dojoReport) {
        try {
            const response = await this.axiosInstance.request(dojoReport.initialRequest);
            if (response.status === 201 && response.data && response.data.requestId) {
                dojoReport.requestId = response.data.requestId;
                return true;
            }
            return false;
        } catch (error) {
            this.handleError(error);
            return false;
        }
    }

    /**
     * @param {DojoTransaction} dojoTransaction
     * @return {Promise<*|undefined>}
     */
    async pollTransaction(dojoTransaction) {
        const request = new DojoTransactionPollRequestRecord(
            this.config,
            dojoTransaction.requestId
        );
        const poll = async (pollRequest) => {
            let response = await this.axiosInstance.request(pollRequest);
            this.$timeout(() => {
                dojoTransaction.addResponse(response);
            });
            if (response.status === 200) {
                if (response.data.notifications[0] === DojoTransactionNotifications.TRANSACTION_FINISHED) {
                    return;
                }
                setTimeout(async () => {
                    await poll(pollRequest);
                }, this.config.pollPeriod);
            }
        };
        // eslint-disable-next-line no-return-await
        return await poll(request);
    }

    /**
     * @param {DojoReport} dojoReport
     * @return {Promise<*|undefined>}
     */
    async pollReport(dojoReport) {
        const request = new DojoTerminalReportPollRequestRecord(
            this.config,
            dojoReport.requestId
        );
        const poll = async (pollRequest) => {
            let response = await this.axiosInstance.request(pollRequest);
            dojoReport.addResponse(response);

            if (response.status === 200) {
                if (response.data.notifications[0] === DojoReportNotifications.REPORT_FINISHED) {
                    return;
                }
                setTimeout(async () => {
                    await poll(pollRequest);
                }, 5000);
            }
        };

        let a = await poll(request);
        return a;
    }

    async sendSignatureRequest(dojoTransaction, accepted) {
        try {
            this.axiosInstance.request(new DojoTransactionSignatureRequestRecord(
                this.config,
                dojoTransaction.requestId,
                accepted
            ));
            return true;
        } catch (error) {
            this.handleError(error);
            return false;
        }
    }

    async sendDeleteRequest(dojoTransaction) {
        try {
            this.axiosInstance.request(new DojoTransactionDeleteRequestRecord(
                this.config,
                dojoTransaction.requestId,
            ));
            return true;
        } catch (error) {
            this.handleError(error);
            return false;
        }
    }

    /**
     *
     * @param response
     * @return {string}
     */
    getStatusDetail(response) {
        let details = [];
        const fields = [
            'location',
            'paymentMethod',
            'primaryAccountNumber',
            'primaryAccountNumberSequence',
            'transactionId',
            'transactionNumber',
            'transactionResult',
            'transactionTime',
            'transactionType',
            'userMessage',
        ];

        fields.forEach((field) => {
            details.push(field + ': ' + response[field]);
        });

        return details.join(', ');
    }

    /**
     * When we receive a 4xx error then we need to display the error and message to the user.
     * When we receive a 5xx error then just display a generic message.
     *
     * @param error
     */
    handleError(error) {
        let userMessage = 'Dojo API error. Please check that your settings are correct.';
        if (error.response && error.response.data && error.response.data.userMessage) {
            userMessage = error.response.data.userMessage;
        }

        throw new Error(userMessage);
    }
}

module.exports = DojoDriver;
